// Fonts (ISO 32000-1 §9): simple fonts (Type1, TrueType), composite fonts (Type0 with CID fonts) and Type 3. Embedded programs give glyph outlines; the
// standard fonts and others without a program are drawn with the browser's own fonts.

import { CffFont } from "./cff";
import { CMap } from "./cmap";
import { baseGlyphName, baseUnicode, glyphToUnicode, unicodeToGlyph, type EncodingName } from "./glyphlist";
import { Dict, PName, Stream, type PObj, type XRef } from "./parse";
import { TrueTypeFont, type FontProgram, type GlyphPath } from "./truetype";
import { Type1Font } from "./type1";

/** One glyph of a shown string. Widths are in thousandths of text space (the font's own 1000-unit em). */
export interface GlyphRec {
    code: number;
    cid: number;
    width: number;
    unicode: string;
    /** A single-byte code 32: word spacing applies. */
    space: boolean;
    /** Vertical writing: the glyph's origin shift (v) and advance (w1y). */
    vx?: number;
    vy?: number;
    adv?: number;
}

export interface Type3Info { charProcs: Dict; matrix: number[]; resources: Dict | null; names: Array<string | undefined> }

const nums = (a: PObj[] | null, xref: XRef): number[] => (a ?? []).map((x) => { const v = xref.resolve(x); return typeof v === "number" ? v : 0; });

export function cssFor(base: string, flags: number): { family: string; weight: string; style: string } {
    const n = base.replace(/^[A-Z]{6}\+/, "");
    const lower = n.toLowerCase();
    const bold = /bold|black|heavy|demi|semibold/.test(lower) || (flags & 0x40000) !== 0;
    const italic = /italic|oblique|slanted/.test(lower) || (flags & 0x40) !== 0;
    let family: string;
    if (/symbol/.test(lower) && !/symbolmt?$/.test(lower) === false) family = "Symbol, 'Segoe UI Symbol'";
    else if (/zapfdingbats|dingbats/.test(lower)) family = "'Segoe UI Symbol', 'Apple Symbols', ZapfDingbats";
    else if (/courier|mono|consolas|typewriter|lucidaconsole|menlo/.test(lower) || (flags & 1)) family = "'Courier New', Courier, monospace";
    else if (/times|serif(?!.*sans)|georgia|garamond|palatino|bookman|century|cambria|minion|didot|baskerville|roman/.test(lower) && !/sans/.test(lower)) family = "'Times New Roman', Times, serif";
    else if (/arial|helvetica|sans|calibri|verdana|tahoma|segoe|trebuchet|myriad|frutiger|gill|futura|roboto|open/.test(lower)) family = "Arial, Helvetica, sans-serif";
    else family = (flags & 2) ? "'Times New Roman', Times, serif" : "Arial, Helvetica, sans-serif";
    return { family, weight: bold ? "700" : "400", style: italic ? "italic" : "normal" };
}

let measureCtx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D | null = null;
function measure(text: string, font: string): number {
    if (!measureCtx) {
        try { measureCtx = new OffscreenCanvas(8, 8).getContext("2d"); } catch { measureCtx = document.createElement("canvas").getContext("2d"); }
    }
    if (!measureCtx) return 500;
    measureCtx.font = font;
    return measureCtx.measureText(text).width;
}

export class PdfFont {
    readonly type3: Type3Info | null = null;
    vertical = false;
    base = "";
    flags = 0;
    private program: FontProgram | null = null;
    private tt: TrueTypeFont | null = null;
    private cff: CffFont | null = null;
    private t1: Type1Font | null = null;
    private composite = false;
    private cmap: CMap | null = null;
    private toUni: CMap | null = null;
    private cidToGid: Uint16Array | null = null;
    private dw = 1000;
    private wmap = new Map<number, number>();
    private dw2: [number, number] = [880, -1000];
    private w2 = new Map<number, [number, number, number]>();
    // simple fonts
    private names: Array<string | undefined> = new Array(256);
    private uni: string[] = new Array(256).fill("");
    private widths: number[] = [];
    private firstChar = 0;
    private missingWidth = 0;
    private hasWidths = false;
    private symbolic = false;
    private hasEncoding = false;
    private pathCache = new Map<number, Path2D | null>();
    css = { family: "Arial, sans-serif", weight: "400", style: "normal" };
    private fontBBox: number[] | null = null;
    readonly embedded: boolean;

    constructor(public readonly dict: Dict, xref: XRef) {
        const subtype = dict.name("Subtype") ?? "Type1";
        this.base = dict.name("BaseFont") ?? "";
        if (subtype === "Type0") {
            this.composite = true;
            this.loadType0(dict, xref);
            this.embedded = this.program !== null;
            return;
        }
        let fd = dict.dict("FontDescriptor");
        this.flags = fd?.num("Flags") ?? 0;
        this.symbolic = (this.flags & 4) !== 0 && (this.flags & 32) === 0;
        this.missingWidth = fd?.num("MissingWidth") ?? 0;
        if (fd) { const bb = nums(fd.arr("FontBBox"), xref); if (bb.length === 4) this.fontBBox = bb; }
        if (subtype === "Type3") {
            const cp = dict.dict("CharProcs");
            const fm = nums(dict.arr("FontMatrix"), xref);
            const names: Array<string | undefined> = new Array(256);
            this.readEncoding(dict, xref, names, null);
            this.type3 = { charProcs: cp ?? new Dict(xref), matrix: fm.length === 6 ? fm : [0.001, 0, 0, 0.001, 0, 0], resources: dict.dict("Resources"), names };
            this.names = names;
            this.readWidths(dict, xref);
            this.embedded = true;
            return;
        }
        fd = fd ?? null;
        this.loadProgram(fd, xref);
        this.embedded = this.program !== null;
        this.css = cssFor(this.base, this.flags);
        this.setupSimple(dict, xref);
        this.readWidths(dict, xref);
    }

    private readWidths(dict: Dict, xref: XRef) {
        this.firstChar = dict.num("FirstChar", 0);
        const w = dict.arr("Widths");
        if (w) { this.widths = nums(w, xref); this.hasWidths = true; }
    }

    private loadProgram(fd: Dict | null, xref: XRef) {
        if (!fd) return;
        try {
            const f2 = fd.get("FontFile2"), f3 = fd.get("FontFile3"), f1 = fd.get("FontFile");
            if (f2 instanceof Stream) { const tt = new TrueTypeFont(f2.bytes()); if (tt.cffTable) { this.cff = new CffFont(tt.cffTable); this.tt = tt; this.program = this.cff; } else { this.tt = tt; this.program = tt; } return; }
            if (f3 instanceof Stream) {
                const bytes = f3.bytes();
                const sub = f3.dict.name("Subtype");
                if (sub === "OpenType" || (bytes[0] === 0x4f && bytes[1] === 0x54) || (bytes[0] === 0 && bytes[1] === 1) || (bytes[0] === 0x74 && bytes[1] === 0x72)) {
                    const tt = new TrueTypeFont(bytes);
                    this.tt = tt;
                    if (tt.cffTable) { this.cff = new CffFont(tt.cffTable); this.program = this.cff; } else this.program = tt;
                } else { this.cff = new CffFont(bytes); this.program = this.cff; }
                return;
            }
            if (f1 instanceof Stream) { this.t1 = new Type1Font(f1.bytes()); this.program = this.t1; }
        } catch { this.program = null; this.tt = null; this.cff = null; this.t1 = null; }
        void xref;
    }

    // ---- simple fonts ---------------------------------------------------------------------------------------------------------------

    private readEncoding(dict: Dict, xref: XRef, names: Array<string | undefined>, builtin: ((c: number) => string | undefined) | null) {
        const enc = dict.get("Encoding");
        let base: EncodingName | null = null;
        let diffs: PObj[] | null = null;
        if (enc instanceof PName) { base = enc.name as EncodingName; this.hasEncoding = true; }
        else if (enc instanceof Dict) {
            const be = enc.name("BaseEncoding");
            if (be) base = be as EncodingName;
            diffs = enc.arr("Differences");
            this.hasEncoding = be !== null || diffs !== null;
        }
        const useBase: EncodingName | null = base && ["WinAnsiEncoding", "MacRomanEncoding", "StandardEncoding", "MacExpertEncoding"].includes(base) ? base : null;
        for (let c = 0; c < 256; c++) {
            let n: string | undefined;
            if (useBase) n = baseGlyphName(useBase, c) || undefined;
            else if (builtin) n = builtin(c);
            else if (!this.symbolic) n = baseGlyphName("StandardEncoding", c) || undefined;
            names[c] = n;
        }
        if (diffs) {
            let code = 0;
            for (const d of diffs) {
                const v = xref.resolve(d);
                if (typeof v === "number") code = v;
                else if (v instanceof PName) { if (code >= 0 && code < 256) names[code] = v.name; code++; }
            }
        }
        for (let c = 0; c < 256; c++) {
            const n = names[c];
            let u = "";
            if (n) { const cp = glyphToUnicode(n); if (cp !== null) u = String.fromCodePoint(cp); }
            else if (useBase) u = baseUnicode(useBase, c);
            this.uni[c] = u;
        }
    }

    private setupSimple(dict: Dict, xref: XRef) {
        let builtin: ((c: number) => string | undefined) | null = null;
        if (this.t1) { const t1 = this.t1; builtin = (c) => t1.nameForCode(c); }
        else if (this.cff && !this.cff.isCID) { const cff = this.cff; builtin = (c) => { const g = cff.builtin.get(c); return g !== undefined ? cff.glyphName(g) : undefined; }; }
        this.readEncoding(dict, xref, this.names, builtin);
        const tu = dict.get("ToUnicode");
        if (tu instanceof Stream) { try { this.toUni = CMap.parseToUnicode(tu.bytes()); } catch { this.toUni = null; } }
    }

    private simpleWidth(code: number): number {
        if (this.hasWidths && code >= this.firstChar && code < this.firstChar + this.widths.length) return this.widths[code - this.firstChar]!;
        if (this.hasWidths && this.missingWidth) return this.missingWidth;
        const gid = this.simpleGid(code);
        if (this.program && gid >= 0) {
            if (this.t1) { const w = this.t1.widthByName(this.names[code] ?? ""); if (w >= 0) return w * this.t1.fontMatrix[0]! * 1000; }
            else { const a = this.program.advance(gid); if (a >= 0) return (a * 1000) / this.program.unitsPerEm; }
        }
        if (this.missingWidth) return this.missingWidth;
        const u = this.simpleUnicode(code);
        if (u) return (measure(u, `${this.css.style} ${this.css.weight} 100px ${this.css.family}`) / 100) * 1000;
        return 500;
    }

    private simpleUnicode(code: number): string {
        const t = this.toUni?.unicode(code);
        if (t) return t;
        return this.uni[code] ?? "";
    }

    /** The glyph id for a simple-font code in the embedded program (-1 when none). */
    private simpleGid(code: number): number {
        const p = this.program;
        if (!p) return -1;
        const name = this.names[code];
        if (this.t1) return name !== undefined ? this.t1.gidByName(name) : -1;
        if (this.cff && !this.tt?.hasOutlines) {
            const cff = this.cff;
            if (cff.isCID) return code < cff.numGlyphs ? code : -1;
            if (name !== undefined) { const g = cff.gidByName(name); if (g >= 0) return g; }
            const b = cff.builtin.get(code);
            if (b !== undefined) return b;
            // OpenType/CFF fonts may also answer through their cmap
            if (this.tt) { const u = this.uni[code]; const g = u ? this.tt.gidByUnicode(u.codePointAt(0)!) : 0; if (g) return g; }
            return name === undefined ? -1 : 0;
        }
        if (this.tt) {
            const tt = this.tt;
            const u = this.uni[code] ? this.uni[code]!.codePointAt(0)! : -1;
            const useNames = this.hasEncoding && !this.symbolic;
            if (useNames || (!this.symbolic && tt.hasCmap(3, 1))) {
                if (u >= 0) { let g = tt.lookup(3, 1, u) || tt.lookup(0, 3, u) || tt.lookup(0, 4, u); if (g) return g; }
                if (name !== undefined) { const g = tt.gidByName(name); if (g > 0) return g; }
                const mac = tt.lookup(1, 0, code);
                if (mac && !this.symbolic) return mac;
                if (u >= 0) { const g = tt.gidByUnicode(u); if (g) return g; }
            }
            // symbolic: the font's own cmap by code
            let g = tt.lookup(3, 0, code) || tt.lookup(3, 0, 0xf000 + code) || tt.lookup(3, 0, 0xf100 + code) || tt.lookup(1, 0, code);
            if (g) return g;
            g = tt.lookup(3, 1, code) || tt.lookup(0, 3, code);
            if (g) return g;
            if (name !== undefined) { g = tt.gidByName(name); if (g > 0) return g; }
            if (tt.cmapCount === 0) return code < tt.numGlyphs ? code : -1;
            return 0;
        }
        return -1;
    }

    // ---- composite fonts -------------------------------------------------------------------------------------------------------------

    private loadType0(dict: Dict, xref: XRef) {
        const enc = dict.get("Encoding");
        if (enc instanceof PName) {
            this.vertical = enc.name.endsWith("-V");
            this.cmap = enc.name.startsWith("Identity") ? CMap.identity(this.vertical) : CMap.identity(this.vertical);
        } else if (enc instanceof Stream) {
            this.cmap = CMap.parse(enc.bytes());
            const wm = enc.dict.num("WMode", 0);
            if (wm === 1) this.vertical = true;
            this.cmap.vertical = this.vertical;
        } else this.cmap = CMap.identity();
        const tu = dict.get("ToUnicode");
        if (tu instanceof Stream) { try { this.toUni = CMap.parseToUnicode(tu.bytes()); } catch { this.toUni = null; } }
        const desc = xref.resolve((dict.arr("DescendantFonts") ?? [])[0] ?? null);
        if (!(desc instanceof Dict)) return;
        this.base = desc.name("BaseFont") ?? this.base;
        const fd = desc.dict("FontDescriptor");
        this.flags = fd?.num("Flags") ?? 0;
        this.css = cssFor(this.base, this.flags);
        this.loadProgram(fd, xref);
        this.dw = desc.num("DW", 1000);
        const w = desc.arr("W");
        if (w) {
            let i = 0;
            while (i < w.length) {
                const a = xref.resolve(w[i]!), b = xref.resolve(w[i + 1]!);
                if (typeof a !== "number") { i++; continue; }
                if (Array.isArray(b)) { b.forEach((x, k) => { const v = xref.resolve(x); if (typeof v === "number") this.wmap.set(a + k, v); }); i += 2; }
                else { const c = xref.resolve(w[i + 2]!); if (typeof b === "number" && typeof c === "number") for (let k = a; k <= b && k - a < 70000; k++) this.wmap.set(k, c); i += 3; }
            }
        }
        const dw2 = nums(desc.arr("DW2"), xref);
        if (dw2.length === 2) this.dw2 = [dw2[0]!, dw2[1]!];
        const w2 = desc.arr("W2");
        if (w2) {
            let i = 0;
            while (i < w2.length) {
                const a = xref.resolve(w2[i]!), b = xref.resolve(w2[i + 1]!);
                if (typeof a !== "number") { i++; continue; }
                if (Array.isArray(b)) { for (let k = 0; k + 2 < b.length; k += 3) this.w2.set(a + k / 3, [b[k] as number, b[k + 1] as number, b[k + 2] as number]); i += 2; }
                else { const [w1, vx, vy] = [xref.resolve(w2[i + 2]!), xref.resolve(w2[i + 3]!), xref.resolve(w2[i + 4]!)]; if (typeof b === "number") for (let k = a; k <= b && k - a < 70000; k++) this.w2.set(k, [w1 as number, vx as number, vy as number]); i += 5; }
            }
        }
        const c2g = desc.get("CIDToGIDMap");
        if (c2g instanceof Stream) { const b = c2g.bytes(); const m = new Uint16Array(b.length >> 1); for (let i = 0; i < m.length; i++) m[i] = (b[2 * i]! << 8) | b[2 * i + 1]!; this.cidToGid = m; }
    }

    // ---- shown strings ----------------------------------------------------------------------------------------------------------------

    decode(s: string): GlyphRec[] {
        const out: GlyphRec[] = [];
        if (this.composite) {
            const cm = this.cmap!;
            for (const [code, len] of cm.decode(s)) {
                const cid = cm.cid(code);
                let u = this.toUni?.unicode(code) ?? "";
                if (!u && this.tt && !this.cff && cid) { /* no Unicode known */ }
                if (!u && this.program && !this.hasToUnicode()) u = "";
                const g: GlyphRec = { code, cid, width: this.wmap.get(cid) ?? this.dw, unicode: u, space: len === 1 && code === 32 };
                if (this.vertical) {
                    const v = this.w2.get(cid);
                    g.adv = v ? v[0] : this.dw2[1];
                    g.vx = v ? v[1] : g.width / 2;
                    g.vy = v ? v[2] : this.dw2[0];
                }
                out.push(g);
            }
            return out;
        }
        for (let i = 0; i < s.length; i++) {
            const code = s.charCodeAt(i) & 255;
            out.push({ code, cid: code, width: this.type3 ? this.type3Width(code) : this.simpleWidth(code), unicode: this.simpleUnicode(code), space: code === 32 });
        }
        return out;
    }
    private hasToUnicode() { return this.toUni !== null && this.toUni.hasUnicode; }

    private type3Width(code: number): number {
        const t = this.type3!;
        const w = this.hasWidths && code >= this.firstChar && code < this.firstChar + this.widths.length ? this.widths[code - this.firstChar]! : 0;
        // Widths are in glyph space: through the font matrix to text space, then to thousandths
        return w * t.matrix[0]! * 1000;
    }

    /** The charproc name of a Type 3 code. */
    type3Proc(code: number): Stream | null {
        const t = this.type3;
        const n = t?.names[code];
        if (!t || !n) return null;
        const p = t.charProcs.get(n);
        return p instanceof Stream ? p : null;
    }

    /** The glyph's outline in em units (1 = the font size), or null to fall back to the browser's text drawing. */
    path(g: GlyphRec): Path2D | null {
        const key = this.composite ? g.cid : g.code;
        if (this.pathCache.has(key)) return this.pathCache.get(key)!;
        let p: Path2D | null = null;
        try { p = this.buildPath(g); } catch { p = null; }
        if (this.pathCache.size > 3000) this.pathCache.clear();
        this.pathCache.set(key, p);
        return p;
    }

    /** True when the program has a glyph for the record (an empty outline such as a space counts). */
    hasGlyph(g: GlyphRec): boolean { return this.program !== null && this.gidOf(g) >= 0; }

    private gidOf(g: GlyphRec): number {
        if (this.composite) {
            const cid = g.cid;
            if (this.cff && !this.tt?.hasOutlines) return this.cff.isCID ? this.cff.gidByCid(cid) : (this.cidToGid ? this.cidToGid[cid] ?? 0 : cid);
            if (this.cidToGid) return this.cidToGid[cid] ?? 0;
            return cid;
        }
        return this.simpleGid(g.code);
    }

    private buildPath(g: GlyphRec): Path2D | null {
        const prog = this.program;
        if (!prog) return null;
        const gid = this.gidOf(g);
        if (gid < 0) return null;
        let gp: GlyphPath | null;
        let scaleX: number, scaleY: number;
        if (this.t1 && !this.composite) {
            gp = this.t1.glyphByName(this.names[g.code] ?? "");
            scaleX = this.t1.fontMatrix[0]!; scaleY = this.t1.fontMatrix[3]!;
        } else if (this.cff && !this.tt?.hasOutlines) {
            gp = this.cff.glyph(gid);
            scaleX = this.cff.fontMatrix[0]!; scaleY = this.cff.fontMatrix[3]!;
        } else {
            gp = prog.glyph(gid);
            scaleX = scaleY = 1 / prog.unitsPerEm;
        }
        if (!gp) return new Path2D();
        const p = new Path2D();
        let i = 0;
        while (i < gp.length) {
            const op = gp[i++]!;
            switch (op) {
                case 0: p.moveTo(gp[i]! * scaleX, gp[i + 1]! * scaleY); i += 2; break;
                case 1: p.lineTo(gp[i]! * scaleX, gp[i + 1]! * scaleY); i += 2; break;
                case 2: p.quadraticCurveTo(gp[i]! * scaleX, gp[i + 1]! * scaleY, gp[i + 2]! * scaleX, gp[i + 3]! * scaleY); i += 4; break;
                case 3: p.bezierCurveTo(gp[i]! * scaleX, gp[i + 1]! * scaleY, gp[i + 2]! * scaleX, gp[i + 3]! * scaleY, gp[i + 4]! * scaleX, gp[i + 5]! * scaleY); i += 6; break;
                case 4: p.closePath(); break;
            }
        }
        return p;
    }

    /** The text to draw with the browser's font when the program has no glyph. */
    fallbackText(g: GlyphRec): string {
        if (this.composite) return g.unicode;
        return this.simpleUnicode(g.code) || g.unicode;
    }

    get fontBox(): number[] | null { return this.fontBBox; }
    /** The browser font to draw fallback text with, at 100 px. */
    cssFont(): string { return `${this.css.style} ${this.css.weight} 100px ${this.css.family}`; }
}

export function loadFont(dict: Dict, xref: XRef): PdfFont { return new PdfFont(dict, xref); }
void unicodeToGlyph;
